import { useEffect, useMemo } from "react";
import { useAthleteStatsStore } from "@/store/api/athleteStore";
import { useBookingsSupabaseStore } from "@/store/api/bookingsSupabaseStore";
import { useUserStore } from "@/store/userStore";
import { SubscriptionStatusAlert } from "@/components/athlete/subscription/SubscriptionStatusAlert";
import { useClientSubscriptionStore } from "@/store/api/clients/clientSubscriptionStore";
import { HeroSection } from "@/components/athlete/dashboard/HeroSection";
import { StatsOverviewCard } from "@/components/athlete/dashboard/StatsOverviewCard";
import { SubscriptionCreditsCard } from "@/components/athlete/dashboard/SubscriptionCreditsCard";
import { QuickAccessLinks } from "@/components/athlete/dashboard/QuickAccessLinks";
import { useSubscriptionCalendarLabels } from "@/hooks/useSubscriptionCalendarLabels";
import { NextSessionCard } from "@/components/athlete/dashboard/NextSessionCard";

const AthleteDashboard = () => {
  const { fetchAthleteStats } = useAthleteStatsStore();
  const { fetchBookings } = useBookingsSupabaseStore();
  const { allSubscriptions, loadClientSubscriptionData } = useClientSubscriptionStore();
  const { gymId, isLoadingGymSwitch } = useUserStore();

  // Un adhérent peut détenir plusieurs abonnements actifs, de plannings
  // différents : chacun mérite sa propre alerte (expiration, séances basses).
  const activeSubscriptions = useMemo(
    () => (allSubscriptions ?? []).filter((s) => s.status === "Active"),
    [allSubscriptions]
  );
  const { calendarLabelFor } = useSubscriptionCalendarLabels(activeSubscriptions, gymId);

  useEffect(() => {
    if (!gymId) return;

    fetchAthleteStats();
    fetchBookings();
    loadClientSubscriptionData();
  }, [fetchAthleteStats, fetchBookings, loadClientSubscriptionData, gymId]);

  if (isLoadingGymSwitch) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="athlete-page-bg min-h-screen flex flex-col w-full">
      <main className="flex-1 px-4 py-5 md:p-6 max-w-7xl mx-auto w-full">
        {/* Ordre aligné sur la maquette : accueil, prochaine séance,
            abonnement, vue d'ensemble, accès rapides. */}
        <div className="space-y-2.5 animate-fade-in pb-20 lg:pb-4">
          <HeroSection />

          <NextSessionCard />

          {activeSubscriptions.map((sub) => (
            <SubscriptionStatusAlert
              key={sub.id}
              subscription={sub}
              calendarLabel={calendarLabelFor(sub.id)}
            />
          ))}

          <SubscriptionCreditsCard />

          <StatsOverviewCard />

          <QuickAccessLinks />
        </div>
      </main>
    </div>
  );
};

export default AthleteDashboard;
